import * as THREE from "three";
import { GLTF, GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";
import constructedRingAsset from "@/assets/models/brotherV7.glb?url";
import textRingFontUrl from "@/assets/fonts/DrukWide-Super-Trial.otf?url";

export { constructedRingAsset };

//////// LIVE TEXT
const textRingFontFamily = "Druk Wide Super";
const textRingHeight = 0.18;
const textRingCanvasScale = 4;
const textRingGeometrySegments = 256;
const textRingPadding = 0.08;
const textRingAutoFitToRepeat = true;
const textRingSlotFill = 0.8;
export const innerTextCopies = 3;

export const outerTextCopies = 3;

export function getDefaultInnerRing(): "film" | "music" {
    return document.documentElement.dataset.site === "music" ? "music" : "film";
}

export function getCustomLogoText(root: HTMLElement) {
    const selector = "[data-custom-logo-text-inner], [data-custom-logo-text-outer]";
    const source = root.matches(selector) ? root : root.querySelector<HTMLElement>(selector);
    const fullPageValue = source?.dataset.customLogoFullPage;

    return {
        inner: (source?.dataset.customLogoTextInner || "").trim().toUpperCase(),
        outer: (source?.dataset.customLogoTextOuter || "").trim().toUpperCase(),
        fullPage: Boolean(fullPageValue) && fullPageValue !== "false" && fullPageValue !== "0",
    };
}

let textRingFontLoadPromise: Promise<void | FontFaceSet> | null = null;

export function loadTextRingFont(isNeeded: boolean) {
    if (!isNeeded || !document.fonts) {
        return Promise.resolve();
    }

    if (textRingFontLoadPromise) {
        return textRingFontLoadPromise;
    }

    if (window.FontFace) {
        const fontFace = new FontFace(textRingFontFamily, `url("${textRingFontUrl}")`, {
            weight: "900",
            style: "normal",
        });

        textRingFontLoadPromise = fontFace
            .load()
            .then(loadedFont => {
                document.fonts.add(loadedFont);
                return document.fonts.ready;
            })
            .catch(error => {
                console.warn(`Could not preload ${textRingFontFamily}. Browser fallback font will be used.`, error);
            });

        return textRingFontLoadPromise;
    }

    textRingFontLoadPromise = document.fonts
        .load(`900 32px "${textRingFontFamily}"`)
        .then(() => document.fonts.ready)
        .catch(error => {
            console.warn(`Could not preload ${textRingFontFamily}. Browser fallback font will be used.`, error);
        });

    return textRingFontLoadPromise;
}

export function createTextSourceMesh(text: string, name: string, copies: number, flipY: boolean, color: number, maxAnisotropy: number) {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    const fontSize = 256 * textRingCanvasScale;
    const padding = fontSize * textRingPadding;

    context!.font = `900 ${fontSize}px "${textRingFontFamily}"`;

    const metrics = context!.measureText(text);
    const ascent = metrics.actualBoundingBoxAscent || fontSize * 0.78;
    const descent = metrics.actualBoundingBoxDescent || fontSize * 0.22;

    canvas.width = Math.ceil(metrics.width + padding * 2);
    canvas.height = Math.ceil(ascent + descent + padding * 2);

    context!.clearRect(0, 0, canvas.width, canvas.height);
    context!.font = `900 ${fontSize}px "${textRingFontFamily}"`;
    //////// White glyphs — the material's color uniform does the tinting.
    context!.fillStyle = "#ffffff";
    context!.textAlign = "left";
    context!.textBaseline = "alphabetic";
    context!.fillText(text, padding, padding + ascent);

    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = maxAnisotropy;
    texture.generateMipmaps = false;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;

    const naturalAspect = canvas.width / canvas.height;
    const naturalWidth = textRingHeight * naturalAspect;
    const maxSlotWidth = ((Math.PI * 2 * constructedRingEndRadius) / copies) * textRingSlotFill;
    const sourceWidth = textRingAutoFitToRepeat ? Math.min(naturalWidth, maxSlotWidth) : naturalWidth;
    const sourceHeight = sourceWidth / naturalAspect;
    const geometry = new THREE.PlaneGeometry(sourceWidth, sourceHeight, textRingGeometrySegments, 1);
    const material = createDrumLogoMaterial(color, texture);
    const mesh = new THREE.Mesh(geometry, material);

    mesh.name = name;
    mesh.rotation.y = flipY ? Math.PI : 0;
    mesh.updateMatrixWorld(true);

    return mesh;
}

//////// SHARED RING GEOMETRY
export const streamWidthScale = 1;
export const streamGapStartScale = 0.1;
export const streamGapEndScale = 1;
export const constructedRingStartRadius = 1.2;
export const constructedRingEndRadius = 0.38;

export interface IStream {
    name: string;
    group: THREE.Group;
    isInnerRing: boolean;
    isTextRing: boolean;
    introRepeatCopies: boolean;
    repeatCopiesVisible: boolean;
    repeatSingleCopyIndex: number;
    sourceGeometry: THREE.BufferGeometry;
    sourceBounds: THREE.Box3;
    material: THREE.Material | THREE.Material[];
    copyCount: number;
    meshes: THREE.Mesh[];
    mergedMesh?: THREE.Mesh | null;
    isMerged?: boolean;
    lastBendWidthScale?: number;
    lastBendGapScale?: number;
    lastBendRadius?: number;
}

export interface IPart {
    name: string;
    text: string;
    copies: number;
    isInnerRing: boolean;
    introRepeatCopies?: boolean;
}

//////// LOADER (shared promise — the GLB is fetched and decoded once per page load)
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath(`${import.meta.env.BASE_URL}draco/`);
dracoLoader.preload();

const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);

let ringGltfPromise: Promise<GLTF> | null = null;

export function getRingGltf(): Promise<GLTF> {
    ringGltfPromise ??= new Promise<GLTF>((resolve, reject) => {
        gltfLoader.load(constructedRingAsset, resolve, undefined, reject);
    });
    return ringGltfPromise;
}

//////// MATERIALS
function hasColor(material: THREE.Material): material is THREE.Material & { color: THREE.Color } {
    return "color" in material;
}

function hasMap(material: THREE.Material): material is THREE.Material & { map: THREE.Texture | null } {
    return "map" in material;
}

function forEachMaterial(material: THREE.Material | THREE.Material[], callback: (material: THREE.Material) => void) {
    if (Array.isArray(material)) {
        material.forEach(callback);
    } else if (material) {
        callback(material);
    }
}

export function setMaterialColor(material: THREE.Material | THREE.Material[], color: number) {
    forEachMaterial(material, item => {
        if (hasColor(item) && item.color.getHex() !== color) {
            item.color.setHex(color);
        }
    });
}

export function setMaterialOpacity(material: THREE.Material | THREE.Material[], opacity: number) {
    const clampedOpacity = THREE.MathUtils.clamp(opacity, 0, 1);
    const isTransparent = clampedOpacity < 0.999;

    forEachMaterial(material, item => {
        item.opacity = clampedOpacity;

        if (item.transparent !== isTransparent) {
            item.transparent = isTransparent;
            item.needsUpdate = true;
        }
    });
}

function disposeMaterial(material: THREE.Material | THREE.Material[], disposeMaps: boolean) {
    forEachMaterial(material, item => {
        if (disposeMaps && hasMap(item) && item.map) {
            item.map.dispose();
        }

        item.dispose();
    });
}

export function createDrumLogoMaterial(color?: number, map?: THREE.Texture) {
    return new THREE.MeshBasicMaterial({
        map: map || null,
        transparent: Boolean(map),
        alphaTest: map ? 0.02 : 0,
        color,
        side: THREE.FrontSide,
    });
}

//////// SOURCE MESHES
export function findMeshByObjectName(root: THREE.Group, objectName: string) {
    const namedObject = root.getObjectByName(objectName);

    if (namedObject instanceof THREE.Mesh) {
        return namedObject;
    }

    let foundMesh: THREE.Mesh | null = null;
    const searchRoot = namedObject || root;

    searchRoot.traverse(child => {
        if (!foundMesh && child instanceof THREE.Mesh && (!namedObject || child.name === objectName)) {
            foundMesh = child;
        }
    });

    return foundMesh;
}

function getSourceGeometry(sourceMesh: THREE.Mesh) {
    sourceMesh.updateWorldMatrix(true, false);

    const geometry = sourceMesh.geometry.clone();
    geometry.applyMatrix4(sourceMesh.matrixWorld);
    geometry.computeBoundingBox();

    return geometry;
}

//////// BENDING FLAT SOURCE GEOMETRY INTO A RING
function updateBentGeometry(targetGeometry: THREE.BufferGeometry, sourceGeometry: THREE.BufferGeometry, sourceBounds: THREE.Box3, flatOffset: number, widthScale: number, circumference: number, radius: number) {
    const sourcePositions = sourceGeometry.attributes.position;
    const targetPositions = targetGeometry.attributes.position;
    const sourceNormals = sourceGeometry.attributes.normal;
    const targetNormals = targetGeometry.attributes.normal;
    const sourceZCenter = (sourceBounds.min.z + sourceBounds.max.z) * 0.5;

    for (let i = 0; i < sourcePositions.count; i++) {
        const x = sourcePositions.getX(i);
        const y = sourcePositions.getY(i);
        const z = sourcePositions.getZ(i) - sourceZCenter;
        const flatX = flatOffset + (x - sourceBounds.min.x) * widthScale;
        const angle = (flatX / circumference) * Math.PI * 2;
        const radialX = Math.sin(angle);
        const radialZ = Math.cos(angle);
        const tangentX = Math.cos(angle);
        const tangentZ = -Math.sin(angle);
        const vertexRadius = radius + z;

        targetPositions.setXYZ(i, radialX * vertexRadius, y, radialZ * vertexRadius);

        if (sourceNormals && targetNormals) {
            const normalX = sourceNormals.getX(i);
            const normalY = sourceNormals.getY(i);
            const normalZ = sourceNormals.getZ(i);
            const bentNormalX = tangentX * normalX + radialX * normalZ;
            const bentNormalY = normalY;
            const bentNormalZ = tangentZ * normalX + radialZ * normalZ;
            const bentNormalLength = Math.sqrt(bentNormalX * bentNormalX + bentNormalY * bentNormalY + bentNormalZ * bentNormalZ) || 1;

            targetNormals.setXYZ(i, bentNormalX / bentNormalLength, bentNormalY / bentNormalLength, bentNormalZ / bentNormalLength);
        }
    }

    targetPositions.needsUpdate = true;

    if (targetNormals) {
        targetNormals.needsUpdate = true;
    }

    targetGeometry.computeBoundingBox();
    targetGeometry.computeBoundingSphere();
}

//////// SPACING REPEATED COPIES AROUND THE RING
export function updateRingStreamWidth(stream: IStream, widthScale: number, gapScale: number, radius: number) {
    const circumference = Math.PI * 2 * radius;
    const sourceWidth = stream.sourceBounds.max.x - stream.sourceBounds.min.x;
    const slotWidth = circumference / stream.copyCount;
    const gap = slotWidth - sourceWidth;
    const scaledSourceWidth = sourceWidth * widthScale;
    const scaledGap = gap * gapScale;
    const scaledSlotWidth = scaledSourceWidth + scaledGap;
    const bendUnchanged = stream.lastBendWidthScale === widthScale && stream.lastBendGapScale === gapScale && stream.lastBendRadius === radius;

    stream.lastBendWidthScale = widthScale;
    stream.lastBendGapScale = gapScale;
    stream.lastBendRadius = radius;

    stream.meshes.forEach((mesh, i) => {
        const flatOffset = i * scaledSlotWidth + scaledGap * 0.5;

        if (stream.introRepeatCopies) {
            mesh.visible = stream.repeatCopiesVisible || i === stream.repeatSingleCopyIndex;
        }

        if (bendUnchanged) {
            return;
        }

        updateBentGeometry(mesh.geometry, stream.sourceGeometry, stream.sourceBounds, flatOffset, widthScale, circumference, radius);
    });
}

//////// BUILDING ONE RING STREAM
export function createRingPart(sourceMesh: THREE.Mesh, part: IPart, radius: number, material: THREE.Material | THREE.Material[]) {
    const group = new THREE.Group();
    const sourceGeometry = getSourceGeometry(sourceMesh);
    const sourceBounds = sourceGeometry.boundingBox;
    const stream: IStream = {
        name: part.name,
        group,
        isInnerRing: part.isInnerRing,
        isTextRing: Boolean(part.text),
        introRepeatCopies: Boolean(part.introRepeatCopies),
        repeatCopiesVisible: !part.introRepeatCopies,
        repeatSingleCopyIndex: Math.floor(part.copies * 0.5),
        sourceGeometry,
        sourceBounds: sourceBounds!,
        material,
        copyCount: part.copies,
        meshes: [],
        mergedMesh: null,
        isMerged: false,
    };

    group.name = part.name;
    group.visible = !part.isInnerRing;

    for (let i = 0; i < part.copies; i++) {
        const ringMesh = new THREE.Mesh(sourceGeometry.clone(), stream.material);

        ringMesh.name = `${part.name}_${i + 1}`;
        stream.meshes.push(ringMesh);
        group.add(ringMesh);
    }

    updateRingStreamWidth(stream, streamWidthScale, streamGapStartScale, radius);

    return { group, stream };
}

export function clearStreams(streams: IStream[]) {
    streams.forEach(stream => {
        stream.meshes.forEach(mesh => mesh.geometry.dispose());
        stream.sourceGeometry.dispose();

        if (stream.mergedMesh) {
            stream.mergedMesh.geometry.dispose();
        }

        disposeMaterial(stream.material, stream.isTextRing);
    });

    streams.length = 0;
}
